import React, { useEffect, useState } from "react";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Stars = ({ value = 0, size = "sm" }) => {
  const full = Math.round(value);
  const sizeCls = size === "lg" ? "text-2xl" : "text-base";
  return (
    <span className={`${sizeCls} tracking-tight`} data-testid="stars-display">
      {[1, 2, 3, 4, 5].map((i) => (
        <span key={i} className={i <= full ? "text-yellow-500" : "text-slate-300"}>
          ★
        </span>
      ))}
    </span>
  );
};

const ReviewsSection = ({ platformId, currentUser, ownerId, isAdmin }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [respondingId, setRespondingId] = useState(null);
  const [responseText, setResponseText] = useState("");

  const reload = () => {
    if (!platformId) return;
    setLoading(true);
    axios
      .get(`${API}/reviews/platform/${platformId}`)
      .then((res) => setData(res.data))
      .catch(() => setData({ reviews: [], stats: { average: 0, count: 0 } }))
      .finally(() => setLoading(false));
  };

  useEffect(() => { reload(); /* eslint-disable-line */ }, [platformId]);

  const isOwner = currentUser && (currentUser.user_id || currentUser.id) === ownerId;
  const canModerate = isAdmin || (currentUser && currentUser.is_admin);

  const submitResponse = async (reviewId) => {
    if (!responseText.trim()) return;
    try {
      await axios.post(`${API}/reviews/${reviewId}/respond`, {
        response: responseText,
        owner_id: currentUser?.user_id || currentUser?.id
      });
      setRespondingId(null);
      setResponseText("");
      reload();
    } catch (e) {
      alert(e.response?.data?.detail || "Errore");
    }
  };

  const deleteReview = async (reviewId) => {
    if (!window.confirm("Cancellare questa recensione? L'azione è irreversibile.")) return;
    try {
      await axios.delete(`${API}/admin/reviews/${reviewId}`);
      reload();
    } catch (e) {
      alert(e.response?.data?.detail || "Errore");
    }
  };

  if (loading) {
    return (
      <div className="mt-6 border-t border-slate-200 pt-6" data-testid="reviews-section-loading">
        <p className="text-slate-500 text-sm">Caricamento recensioni...</p>
      </div>
    );
  }

  const { reviews = [], stats = { average: 0, count: 0 } } = data || {};

  return (
    <div className="mt-6 border-t border-slate-200 pt-6" data-testid="reviews-section">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-bold text-slate-800">Recensioni</h3>
        {stats.count > 0 && (
          <div className="flex items-center gap-2">
            <Stars value={stats.average} size="lg" />
            <span className="font-semibold text-slate-700">{stats.average.toFixed(1)}</span>
            <span className="text-slate-400 text-sm">({stats.count} {stats.count === 1 ? 'recensione' : 'recensioni'})</span>
          </div>
        )}
      </div>

      {stats.count === 0 ? (
        <div className="bg-slate-50 border border-slate-200 rounded-lg p-6 text-center" data-testid="reviews-empty">
          <Stars value={0} size="lg" />
          <p className="text-slate-500 mt-2 text-sm">Ancora nessuna recensione per questa piattaforma.</p>
          <p className="text-slate-400 text-xs mt-1">Sarai il primo a lasciarne una dopo il noleggio.</p>
        </div>
      ) : (
        <div className="space-y-3 max-h-96 overflow-y-auto pr-2">
          {reviews.map((r) => (
            <div key={r.id} className="bg-white border border-slate-200 rounded-lg p-4" data-testid={`review-item-${r.id}`}>
              <div className="flex items-center justify-between mb-1">
                <div className="font-semibold text-slate-800 text-sm">{r.author_display_name}</div>
                <div className="flex items-center gap-2">
                  <Stars value={r.rating} />
                  {canModerate && (
                    <button
                      onClick={() => deleteReview(r.id)}
                      className="text-red-500 hover:text-red-700 text-xs px-2 py-1 rounded hover:bg-red-50"
                      title="Cancella recensione"
                      data-testid={`admin-delete-review-${r.id}`}
                    >
                      🗑
                    </button>
                  )}
                </div>
              </div>
              <div className="text-xs text-slate-400 mb-2">
                {new Date(r.created_at).toLocaleDateString('it-IT', { day: 'numeric', month: 'long', year: 'numeric' })}
              </div>
              {r.comment && <p className="text-slate-700 text-sm leading-relaxed">{r.comment}</p>}
              {r.owner_response && (
                <div className="mt-3 ml-4 pl-3 border-l-2 border-orange-300 bg-orange-50 p-2 rounded">
                  <div className="text-xs font-semibold text-orange-700 mb-1">
                    Risposta dell'azienda:
                    {r.owner_response_at && (
                      <span className="ml-2 text-slate-500 font-normal">
                        {new Date(r.owner_response_at).toLocaleDateString('it-IT', { day: 'numeric', month: 'long', year: 'numeric' })}
                      </span>
                    )}
                  </div>
                  <p className="text-sm text-slate-700">{r.owner_response}</p>
                </div>
              )}
              {/* Owner response form */}
              {!r.owner_response && isOwner && (
                <div className="mt-3">
                  {respondingId === r.id ? (
                    <div className="bg-slate-50 border border-slate-200 rounded p-2">
                      <textarea
                        value={responseText}
                        onChange={(e) => setResponseText(e.target.value)}
                        placeholder="Scrivi una risposta professionale..."
                        className="w-full h-20 p-2 text-sm border border-slate-300 rounded resize-none focus:outline-none focus:border-orange-500"
                        data-testid={`owner-response-textarea-${r.id}`}
                      />
                      <div className="flex justify-end gap-2 mt-2">
                        <button
                          onClick={() => { setRespondingId(null); setResponseText(""); }}
                          className="text-sm px-3 py-1 text-slate-500 hover:bg-slate-200 rounded"
                        >
                          Annulla
                        </button>
                        <button
                          onClick={() => submitResponse(r.id)}
                          disabled={!responseText.trim()}
                          className="text-sm px-3 py-1 bg-orange-600 text-white rounded hover:bg-orange-700 disabled:opacity-50"
                          data-testid={`submit-response-${r.id}`}
                        >
                          Pubblica risposta
                        </button>
                      </div>
                    </div>
                  ) : (
                    <button
                      onClick={() => { setRespondingId(r.id); setResponseText(""); }}
                      className="text-xs text-orange-600 hover:text-orange-700 hover:underline"
                      data-testid={`reply-review-btn-${r.id}`}
                    >
                      ↳ Rispondi
                    </button>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default ReviewsSection;
